import React, { KeyboardEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Button, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Select, { SingleValue } from 'react-select';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { CustomButton } from '../../components/CustomButton';
import { AuthContext } from '../../context/AuthContext';
import { ShortDevicePassport } from '../../models/warehouse-models';
import {
    getPassportByPassportIdAndType,
    getQuantityCount,
    importWarehousePdfDocument,
    useGetAllShortPassports as getAllShortPassports,
    useGetPassportTypesForWarehouse,
} from '../../utils/warehouses-hooks';
import { resourceCache } from 'use-async-resource';
import { Role } from '../../utils/Enums/Roles';
import { getGeneratedBatchCode } from '../../utils/batch-code';
import { getWarehouseImportConflictMessage } from '../../utils/warehouse-import-error';
import { toast } from 'react-toastify';

interface ManualWarehouseQuantityRow {
    id: number;
    deliveryPrice: string;
    passport: ShortDevicePassport | null;
    quantity: string;
    quantityUnit: QuantityUnit;
    hasContainer: boolean;
}

const createEmptyRow = (id: number): ManualWarehouseQuantityRow => ({
    id,
    deliveryPrice: '',
    passport: null,
    quantity: '5',
    quantityUnit: quantityUnits.ml,
    hasContainer: false,
});

const quantityUnits = {
    count: 'COUNT',
    ml: 'ML',
} as const;

type QuantityUnit = typeof quantityUnits[keyof typeof quantityUnits];

const getGeneratedCode = () => {
    return getGeneratedBatchCode();
};

const normalizeDecimalValue = (value: string) => value.replace(/\s/g, '').replace(/,/g, '.');
const isOilPassport = (passport?: ShortDevicePassport | null) => passport?.type === 'OIL';
const supportsBottle = (passport?: ShortDevicePassport | null) =>
    passport?.type === 'DEVICE'
    && passport.supportedContainerType === 'BOTTLE'
    && passport.emptyContainerPassportId != null;
const isLiquidMillilitrePassport = (passport?: ShortDevicePassport | null) =>
    isOilPassport(passport) && passport?.trackingType === 'LIQUID_ML';
const getEnteredQuantity = (row: ManualWarehouseQuantityRow) =>
    Number(normalizeDecimalValue(row.quantity));
const hasValidMillilitreStep = (row: ManualWarehouseQuantityRow) => {
    if (row.quantityUnit !== quantityUnits.ml) return true;
    const quantity = getEnteredQuantity(row);
    return Number.isInteger(quantity) && quantity > 0 && quantity % 5 === 0;
};
const hasValidCount = (row: ManualWarehouseQuantityRow) => {
    if (row.quantityUnit !== quantityUnits.count) return true;
    const quantity = getEnteredQuantity(row);
    return Number.isInteger(quantity) && quantity > 0;
};
const hasValidQuantity = (row: ManualWarehouseQuantityRow) =>
    hasValidMillilitreStep(row) && hasValidCount(row);
const getImportQuantity = (row: ManualWarehouseQuantityRow) => getEnteredQuantity(row) || 0;

const getPassportOptionValue = (passport: ShortDevicePassport) => String(passport.id ?? passport.passportId ?? '');

const getPassportQuantityDefaults = (
    passport: ShortDevicePassport,
): Pick<ManualWarehouseQuantityRow, 'quantityUnit' | 'quantity'> => {
    const quantityUnit = isLiquidMillilitrePassport(passport)
        ? quantityUnits.ml
        : quantityUnits.count;
    const quantity = quantityUnit === quantityUnits.ml ? '5' : '1';

    return { quantityUnit, quantity };
};

interface ManualWarehouseQuantityImportProps {
    onImportSuccess?: () => Promise<void> | void;
    warehouseId: number;
}

export const ManualWarehouseQuantityImport = ({
    onImportSuccess,
    warehouseId,
}: ManualWarehouseQuantityImportProps) => {
    const { t } = useTranslation('translation');
    const translate = useCallback((key: string) => t(`table.${key}`), [t]);
    const translateCommon = useCallback((key: string) => t(`common.${key}`), [t]);
    const { hasPermission } = useContext(AuthContext);
    const [showModal, setShowModal] = useState(false);
    const [documentNumber, setDocumentNumber] = useState(getGeneratedCode());
    const [rows, setRows] = useState<ManualWarehouseQuantityRow[]>([createEmptyRow(1)]);
    const [allPassports, setAllPassports] = useState<ShortDevicePassport[]>([]);
    const [isLoadingPassports, setIsLoadingPassports] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const supplierName = translate('warehouseManualQuantitySupplier');
    const canAddQuantities = hasPermission([Role.WAREHOUSE, Role.ADMIN]);
    const totalQuantity = useMemo(
        () => rows.reduce((sum, row) => sum + (Number(normalizeDecimalValue(row.quantity)) || 0), 0),
        [rows],
    );

    useEffect(() => {
        if (!showModal || allPassports.length > 0 || isLoadingPassports) {
            return;
        }

        setIsLoadingPassports(true);
        getAllShortPassports(false)
            .then((passports) => setAllPassports([...passports].sort((a, b) => a.name.localeCompare(b.name))))
            .catch(() => setErrorMessage(translate('warehousePdfImportPassportsError')))
            .finally(() => setIsLoadingPassports(false));
    }, [allPassports.length, isLoadingPassports, showModal, translate]);

    const resetForm = () => {
        setDocumentNumber(getGeneratedCode());
        setRows([createEmptyRow(1)]);
        setErrorMessage('');
    };

    const openModal = () => {
        resetForm();
        setShowModal(true);
    };

    const closeModal = () => {
        if (!isSaving) {
            setShowModal(false);
        }
    };

    const updateRow = (index: number, nextRow: Partial<ManualWarehouseQuantityRow>) => {
        setRows((currentRows) =>
            currentRows.map((row, rowIndex) => (rowIndex === index ? { ...row, ...nextRow } : row)),
        );
    };

    const cachePassportDetails = (passport: ShortDevicePassport) => {
        setAllPassports((passports) =>
            passports.map((option) =>
                getPassportOptionValue(option) === getPassportOptionValue(passport)
                    ? {
                        ...option,
                        ...passport,
                }
                    : option,
            ),
        );

        setRows((currentRows) =>
            currentRows.map((row) => {
                if (!row.passport || getPassportOptionValue(row.passport) !== getPassportOptionValue(passport)) {
                    return row;
                }

                return {
                    ...row,
                    passport: {
                        ...row.passport,
                        ...passport,
                    },
                };
            }),
        );
    };

    const loadOilPassportDetails = async (passport: ShortDevicePassport) => {
        if (!isOilPassport(passport) || passport.oilCapacity || !passport.id) {
            return passport;
        }

        const detailedPassport = await getPassportByPassportIdAndType(passport.id, 'OIL');

        const mergedPassport = { ...passport, ...detailedPassport};

        cachePassportDetails(mergedPassport);

        return mergedPassport;
    };

    const handlePassportChange = async (index: number, passport: SingleValue<ShortDevicePassport>) => {
        if (!passport) {
            updateRow(index, { passport: null, quantityUnit: quantityUnits.ml, quantity: '5', hasContainer: false });

            return;
        }

        const selectedPassportValue = getPassportOptionValue(passport);

        updateRow(index, { passport, hasContainer: supportsBottle(passport) });

        try {
            const resolvedPassport = await loadOilPassportDetails(passport);

            const quantityDefaults = getPassportQuantityDefaults(resolvedPassport);

            setRows((currentRows) =>
                currentRows.map((row, rowIndex) => {
                    if (
                        rowIndex !== index ||
                        !row.passport ||
                        getPassportOptionValue(row.passport) !== selectedPassportValue
                    ) {
                        return row;
                    }

                    return {
                        ...row,
                        passport: resolvedPassport,
                        ...quantityDefaults,
                        hasContainer: supportsBottle(resolvedPassport),
                    };
                })
            );
        } catch {
            setErrorMessage(translate('warehousePdfImportPassportsError'));
        }
    };

    const handleQuantityUnitChange = (index: number, quantityUnit: QuantityUnit) => updateRow(index, { quantityUnit });

    const addRow = () => setRows((currentRows) => currentRows.concat(createEmptyRow(currentRows.at(-1)!.id + 1)));

    const removeRow = (index: number) => setRows((currentRows) => currentRows.filter((_, i) => i !== index));

    const handleEnterAsAddRow = (event: KeyboardEvent<HTMLElement>) => {
        if (event.key !== 'Enter') {
            return;
        }

        event.preventDefault();
        addRow();
    };

    const buildPayload = () => ({
        documentNumber,
        supplierName,
        warehouseId,
        items: rows
            .filter((row) => row.passport)
            .map((row, index) => ({
                batchCode: `${getGeneratedBatchCode('L')}-${index + 1}`,
                description: row.passport?.name || '',
                passport: row.passport
                    ? {
                          id: row.passport.id,
                          passportId: row.passport.passportId,
                          name: row.passport.name,
                      }
                    : undefined,
                deliveryPrice: normalizeDecimalValue(row.deliveryPrice),
                quantity: getImportQuantity(row),
                quantityUnit: row.quantityUnit,
                hasContainer: supportsBottle(row.passport) ? row.hasContainer : false,
            })),
    });

    const handleSave = async () => {
        const validRows = rows.filter((row) => row.passport && getImportQuantity(row) > 0);

        if (validRows.length === 0) {
            setErrorMessage(translate('warehousePdfImportNoItems'));
            return;
        }

        if (validRows.some((row) => row.quantityUnit === quantityUnits.ml && !row.passport?.oilCapacity)) {
            setErrorMessage(translate('warehousePdfImportPassportsError'));
            return;
        }

        if (validRows.some((row) => !hasValidMillilitreStep(row))) {
            setErrorMessage(translate('warehouseManualQuantityInvalidMlStep'));
            return;
        }

        if (validRows.some((row) => !hasValidCount(row))) {
            setErrorMessage(translate('warehouseManualQuantityInvalidCount'));
            return;
        }

        setIsSaving(true);
        setErrorMessage('');

        let imported = false;
        try {
            await importWarehousePdfDocument(buildPayload());
            imported = true;
            setShowModal(false);
            resourceCache(getQuantityCount).clear();
            resourceCache(useGetPassportTypesForWarehouse).clear();
            await onImportSuccess?.();
        } catch (error) {
            if (imported) {
                toast.warning(translate('warehousePdfImportRefreshError'));
            } else {
                setErrorMessage(getWarehouseImportConflictMessage(error, t)
                    ?? translate('warehousePdfImportSaveError'));
            }
        } finally {
            setIsSaving(false);
        }
    };

    const getSelectedPassport = (
        selectedPassport: ShortDevicePassport | null
    ): ShortDevicePassport | null => {
        if (!selectedPassport) {
            return null;
        }

        const selectedValue = getPassportOptionValue(selectedPassport);

        return (
            allPassports.find(
                (passport) =>
                    getPassportOptionValue(passport) === selectedValue
            ) ?? null
        );
    };

    return (
        <>
            <CustomButton
                disabled={!canAddQuantities}
                icon={faPlus}
                title={translateCommon('addQuantity')}
                iconClass='me-2'
                onClick={openModal}
                className='btn-success text-nowrap outline-none px-4'
            />

            <Modal show={showModal} onHide={closeModal} backdrop='static' size='xl' centered className='custom-modal'>
                <Modal.Header closeButton={!isSaving}>
                    <Modal.Title id='contained-modal-title-vcenter'>{translateCommon('addQuantity')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className='d-flex flex-wrap gap-3 mb-3'>
                        <Form.Group className='flex-fill' style={{ minWidth: '220px' }}>
                            <Form.Label>{translate('warehousePdfImportDocumentNumber')}</Form.Label>
                            <Form.Control value={documentNumber} readOnly disabled={isSaving} />
                        </Form.Group>
                        <Form.Group className='flex-fill' style={{ minWidth: '220px' }}>
                            <Form.Label>{translate('warehousePdfImportSupplier')}</Form.Label>
                            <Form.Control value={supplierName} readOnly disabled={isSaving} />
                        </Form.Group>
                    </div>

                    <Table responsive striped bordered hover size='sm' className='align-middle small'>
                        <thead>
                        <tr>
                            <th style={{ width: '48%' }}>{translate('warehousePdfImportPassport')}</th>
                            <th className='text-center' style={{ width: '150px' }}>
                                {translate('warehousePdfImportDeliveryPrice')}
                            </th>
                            <th className='text-center' style={{ width: '210px' }}>
                                {translate('warehousePdfImportQuantity')}
                            </th>
                            <th className='text-center' style={{ width: '120px' }}>{translate('hasBottle')}</th>
                            <th className='text-center' style={{ width: '64px' }} />
                        </tr>
                        </thead>
                        <tbody>
                        {rows.map((row, index) => (
                            <tr key={row.id}>
                                <td>
                                    <Select<ShortDevicePassport, false>
                                        className='text-start'
                                        isClearable
                                        isDisabled={isLoadingPassports || isSaving}
                                        isSearchable
                                        maxMenuHeight={180}
                                        menuPortalTarget={document.body}
                                        onChange={(passport) =>
                                            handlePassportChange(index, passport)
                                        }
                                        options={allPassports}
                                        placeholder={
                                            isLoadingPassports
                                                ? translate('warehousePdfImportLoadingPassports')
                                                : translate('warehousePdfImportSelectPassport')
                                        }
                                        styles={{
                                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                            control: (base) => ({ ...base, minHeight: 36 }),
                                        }}
                                        getOptionLabel={(passport) => passport.name}
                                        getOptionValue={getPassportOptionValue}
                                        value={getSelectedPassport(row.passport)}
                                    />
                                </td>
                                <td>
                                    <Form.Control
                                        className='px-1 text-center'
                                        type='text'
                                        inputMode='decimal'
                                        disabled={isSaving}
                                        value={row.deliveryPrice}
                                        onChange={(event) => updateRow(index, { deliveryPrice: event.target.value })}
                                        onKeyDown={handleEnterAsAddRow}
                                    />
                                </td>
                                <td>
                                    <div className='d-flex gap-2'>
                                        <Form.Control
                                            className='px-1 text-center'
                                            type='number'
                                            inputMode='numeric'
                                            min={row.quantityUnit === quantityUnits.ml ? 5 : 1}
                                            step={row.quantityUnit === quantityUnits.ml ? 5 : 1}
                                            isInvalid={row.quantity.trim() !== '' && !hasValidQuantity(row)}
                                            disabled={isSaving}
                                            value={row.quantity}
                                            onChange={(event) => updateRow(index, { quantity: event.target.value })}
                                            onKeyDown={handleEnterAsAddRow}
                                            style={{ minWidth: 0, flex: '1 1 auto' }}
                                        />
                                        <Form.Select
                                            className='px-1'
                                            disabled={isSaving || !isLiquidMillilitrePassport(row.passport)}
                                            value={row.quantityUnit}
                                            onChange={(event) =>
                                                handleQuantityUnitChange(index, event.target.value as QuantityUnit)
                                            }
                                            style={{ width: 54, minWidth: 54, flex: '0 0 54px' }}
                                        >
                                            <option value={quantityUnits.count}>{translateCommon('pcs')}</option>
                                            <option value={quantityUnits.ml}>{translateCommon('ml')}</option>
                                        </Form.Select>
                                    </div>
                                    {row.quantity.trim() !== '' && !hasValidMillilitreStep(row) && (
                                        <Form.Text className='text-danger'>
                                            {translate('warehouseManualQuantityInvalidMlStep')}
                                        </Form.Text>
                                    )}
                                    {row.quantity.trim() !== '' && !hasValidCount(row) && (
                                        <Form.Text className='text-danger'>
                                            {translate('warehouseManualQuantityInvalidCount')}
                                        </Form.Text>
                                    )}
                                </td>
                                <td className='text-center'>
                                    <Form.Check
                                        className='d-flex justify-content-center mb-0'
                                        type='checkbox'
                                        disabled={isSaving || !supportsBottle(row.passport)}
                                        checked={supportsBottle(row.passport) && row.hasContainer}
                                        onChange={(event) =>
                                            updateRow(index, { hasContainer: event.currentTarget.checked })
                                        }
                                        title={translate('hasBottle')}
                                    />
                                </td>
                                <td className='text-center'>
                                    <Button
                                        style={{ width: '27px', marginRight: '5px' }}
                                        variant='danger'
                                        size='sm'
                                        type='button'
                                        disabled={isSaving || rows.length === 1}
                                        onClick={() => removeRow(index)}
                                        title={translate('warehouseManualQuantityRemoveRow')}
                                    >
                                        -
                                    </Button>
                                    <Button
                                        style={{ width: '27px'}}
                                        variant='success'
                                        size='sm'
                                        type='button'
                                        disabled={isSaving}
                                        onClick={addRow}
                                        title={translate('warehouseManualQuantityAddRow')}
                                    >
                                        +
                                    </Button>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </Table>

                    {errorMessage && <p className='text-danger mb-2'>{errorMessage}</p>}
                    <p className='text-end fw-bold mb-0'>
                        {translate('warehousePdfImportTotal')}: {totalQuantity}
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant='secondary' onClick={closeModal} disabled={isSaving}>
                        {translate('warehousePdfImportCancel')}
                    </Button>
                    <Button variant='success' onClick={handleSave} disabled={isSaving}>
                        {isSaving ? <Spinner animation='border' size='sm' /> : translate('warehousePdfImportSave')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};
